// The local practitioner profile's licence (docs/plans/phase1-consultation.md §2.13, P13):
// its state on a day, and the words prints carry. Pure: the caller passes the instant.
//
// Dates are the last day the licence is valid, inclusive, compared on the East Africa Time
// calendar day, the same way the SHA claim readiness compares them.

import { compareDays, daysBetween, eatDayOf, parseIsoDay, type CalendarDay } from '../time.ts';

/** The licensing councils (glossary in CLAUDE.md), plus "other" for anyone else. */
export const LICENCE_BODIES = ['kmpdc', 'nck', 'coc', 'ppb', 'kmlttb', 'other'] as const;
export type LicenceBody = (typeof LICENCE_BODIES)[number];

export const LICENCE_BODY_LABELS: Record<LicenceBody, string> = {
  kmpdc: 'KMPDC',
  nck: 'NCK',
  coc: 'COC',
  ppb: 'PPB',
  kmlttb: 'KMLTTB',
  other: 'Other council',
};

/** "Expires within 30 days" (plan P13): a product setting, this is its starting value. */
export const DEFAULT_LICENCE_WARNING_DAYS = 30;
export const MAX_LICENCE_WARNING_DAYS = 365;

export const LICENCE_STATES = [
  'no_profile',
  'not_recorded',
  'expired',
  'expiring',
  'valid',
] as const;
export type LicenceState = (typeof LICENCE_STATES)[number];

export const LICENCE_STATE_LABELS: Record<LicenceState, string> = {
  no_profile: 'No practitioner details',
  not_recorded: 'Expiry not recorded',
  expired: 'Expired',
  expiring: 'Expires soon',
  valid: 'Valid',
};

export interface LicenceStatus {
  readonly state: LicenceState;
  readonly expiresOn: CalendarDay | null;
  /** Whole days from today (EAT) to the expiry day; negative once expired. */
  readonly daysLeft: number | null;
}

export function licenceStatus(input: {
  hasProfile: boolean;
  /** `YYYY-MM-DD`, as stored. */
  expiresOn: string | null;
  at: Date;
  warningDays: number;
}): LicenceStatus {
  if (!input.hasProfile) return { state: 'no_profile', expiresOn: null, daysLeft: null };
  const parsed = input.expiresOn === null ? null : parseIsoDay(input.expiresOn);
  if (!parsed?.ok) {
    return { state: 'not_recorded', expiresOn: null, daysLeft: null };
  }
  const today = eatDayOf(input.at);
  const expiresOn = parsed.value;
  const daysLeft = daysBetween(today, expiresOn);
  if (compareDays(today, expiresOn) > 0) return { state: 'expired', expiresOn, daysLeft };
  if (daysLeft <= input.warningDays) return { state: 'expiring', expiresOn, daysLeft };
  return { state: 'valid', expiresOn, daysLeft };
}

/** "Name, Cadre" as prescriptions, sick sheets and referrals print it. */
export function practitionerDisplay(name: string, cadre: string | null): string {
  return cadre === null || cadre.trim() === '' ? name : `${name}, ${cadre}`;
}

/** "COC licence TEST-COC-0001"; null when there is no number to print. */
export function licenceText(body: LicenceBody | null, number: string | null): string | null {
  if (number === null || number.trim() === '') return null;
  if (body === null || body === 'other') return `Licence ${number.trim()}`;
  return `${LICENCE_BODY_LABELS[body]} licence ${number.trim()}`;
}
